import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChartLineUp, Scales, Buildings, Calculator } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { formatCurrency } from "../lib/currency";
import { useAuth } from "../lib/auth";
import { StandardReports } from "../components/StandardReports";

const TABS = ["P&L", "Balance Sheet", "Cash Flow", "IFRS 9 ECL", "IFRS 15 Revenue", "IFRS 16 Leases", "Reports"];

export default function IFRSPage() {
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "P&L");
  const { user } = useAuth();
  const cur = user?.tenant?.preferred_currency || "OMR";
  return (
    <div className="p-6 sm:p-8" data-testid="ifrs-page">
      <div className="mb-6">
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">MODULE / IF · IFRS REPORTING</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-1 flex items-center gap-3">
          IFRS Reports.
          <span className="text-xs font-mono px-2 py-1 bg-[#FF3E00] text-white uppercase tracking-widest">IFRS 9 · 15 · 16</span>
        </h1>
        <div className="text-sm text-zinc-500 mt-2 max-w-2xl">
          P&L / Balance Sheet / Cash Flow generated from your GL. Plus IFRS 9 Expected Credit Loss on AR, IFRS 15 revenue recognition, and IFRS 16 lease liability + ROU asset schedules.
        </div>
      </div>
      <div className="flex border-b border-zinc-950 mb-0 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`ifrs-tab-${t.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-xs font-semibold tracking-[0.1em] uppercase border-r border-zinc-950 transition-colors whitespace-nowrap ${
              tab === t ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="border-x border-b border-zinc-950 min-h-[60vh]">
        {tab === "P&L" && <PLPanel cur={cur} />}
        {tab === "Balance Sheet" && <BSPanel cur={cur} />}
        {tab === "Cash Flow" && <CFPanel cur={cur} />}
        {tab === "IFRS 9 ECL" && <ECLPanel cur={cur} />}
        {tab === "IFRS 15 Revenue" && <IFRS15Panel cur={cur} />}
        {tab === "IFRS 16 Leases" && <IFRS16Panel cur={cur} />}
        {tab === "Reports" && <StandardReports module="ifrs" />}
      </div>
    </div>
  );
}

function StatementSection({ title, rows, total, cur, accent }) {
  return (
    <div className="mb-5">
      <div className={`flex justify-between border-b-2 border-zinc-950 pb-1 mb-2 ${accent ? "text-[#FF3E00]" : ""}`}>
        <div className="font-mono text-[10px] tracking-widest uppercase">{title}</div>
        <div className="font-semibold tnum">{formatCurrency(total, cur)}</div>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-zinc-400 py-2">No entries.</div>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.code}-${i}`} className="border-b border-zinc-100">
                <td className="py-1.5 text-xs font-mono text-zinc-500 w-16">{r.code}</td>
                <td className="py-1.5">{r.name}</td>
                <td className="py-1.5 text-right tnum">{formatCurrency(r.amount, cur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PLPanel({ cur }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/ifrs/statements/pl").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  return (
    <div className="p-6" data-testid="ifrs-pl-panel">
      <div className="mb-4 text-xs font-mono text-zinc-400">STATEMENT OF PROFIT OR LOSS · as of {d.period_to}</div>
      <StatementSection title="Revenue" rows={d.revenue} total={d.revenue_total} cur={cur} />
      <StatementSection title="Expenses" rows={d.expenses} total={d.expenses_total} cur={cur} />
      <div className="bg-zinc-950 text-white px-5 py-4 flex justify-between items-center">
        <div className="font-mono text-[10px] tracking-widest">NET PROFIT</div>
        <div className="font-display font-black text-2xl tnum">{formatCurrency(d.net_profit, cur)}</div>
      </div>
    </div>
  );
}

function BSPanel({ cur }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/ifrs/statements/balance-sheet").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  return (
    <div className="p-6" data-testid="ifrs-bs-panel">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-xs font-mono text-zinc-400">STATEMENT OF FINANCIAL POSITION · as of {d.period_to}</div>
        <span className={`text-[10px] font-mono px-2 py-0.5 ${d.balanced ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-[#D0021B]"}`}>
          {d.balanced ? "BALANCED ✓" : "UNBALANCED"}
        </span>
      </div>
      <StatementSection title="Assets" rows={d.assets} total={d.assets_total} cur={cur} />
      <StatementSection title="Liabilities" rows={d.liabilities} total={d.liabilities_total} cur={cur} />
      <StatementSection title="Equity" rows={d.equity} total={d.equity_total} cur={cur} />
    </div>
  );
}

function CFPanel({ cur }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/ifrs/statements/cash-flow").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  return (
    <div className="p-6" data-testid="ifrs-cf-panel">
      <div className="mb-4 text-xs font-mono text-zinc-400">STATEMENT OF CASH FLOWS · indirect method · as of {d.period_to}</div>
      <div className="space-y-2 text-sm">
        <Row k="Net Profit" v={d.net_profit} cur={cur} />
        {Object.entries(d.adjustments || {}).map(([k, v]) => (
          <Row key={k} k={k.replace(/_/g, " ")} v={v} cur={cur} small />
        ))}
        <Row k="Cash from Operating Activities" v={d.operating} cur={cur} accent />
        <Row k="Cash from Investing Activities" v={d.investing} cur={cur} />
        <Row k="Cash from Financing Activities" v={d.financing} cur={cur} />
        <div className="border-t border-zinc-950 pt-2 mt-2">
          <Row k="Net Change in Cash" v={d.net_change} cur={cur} big />
          <Row k="Cash at End of Period" v={d.cash_at_end} cur={cur} big />
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, cur, accent, small, big }) {
  return (
    <div className={`flex justify-between ${small ? "pl-4 text-zinc-500" : ""} ${accent ? "text-[#FF3E00] font-semibold" : ""}`}>
      <span className="capitalize">{k}</span>
      <span className={`tnum ${big ? "font-display text-lg font-black" : ""}`}>{formatCurrency(v, cur)}</span>
    </div>
  );
}

function ECLPanel({ cur }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const compute = () => api.get("/ifrs/ifrs9/ecl").then((r) => setD(r.data));
  useEffect(() => { compute(); }, []);

  const post = async () => {
    setBusy(true);
    try {
      const period = new Date().toISOString().slice(0, 7);   // YYYY-MM
      const r = await api.post("/ifrs/ifrs9/post-journal", { period });
      if (r.data.no_change) {
        toast.info(r.data.message || "No change to post");
      } else {
        toast.success(`Journal ${r.data.journal_no} posted · delta ${r.data.delta}`);
      }
      await compute();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!d) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  return (
    <div className="p-6" data-testid="ifrs9-ecl-panel">
      <div className="mb-4 flex items-baseline justify-between">
        <div className="text-xs font-mono text-zinc-400">EXPECTED CREDIT LOSS · IFRS 9 · as of {d.as_of}</div>
        <div className="flex items-center gap-2">
          <button onClick={compute} className="text-[10px] font-mono border border-zinc-950 px-3 py-1 hover:bg-zinc-950 hover:text-white" data-testid="ecl-recompute">RECOMPUTE</button>
          <button onClick={post} disabled={busy} className="text-[10px] font-mono bg-[#FF3E00] text-white px-3 py-1 disabled:opacity-30" data-testid="ecl-post-journal-btn">
            {busy ? "POSTING…" : "POST JOURNAL"}
          </button>
        </div>
      </div>
      <table className="w-full text-sm mb-4">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-3 py-2">Bucket (DPD)</th>
            <th className="text-right px-3 py-2">Loss %</th>
            <th className="text-right px-3 py-2"># Invoices</th>
            <th className="text-right px-3 py-2">Gross Exposure</th>
            <th className="text-right px-3 py-2">ECL Provision</th>
          </tr>
        </thead>
        <tbody data-testid="ecl-tbody">
          {d.buckets.map((b) => (
            <tr key={b.bucket} className="border-b border-zinc-100">
              <td className="px-3 py-2 font-mono text-xs">{b.bucket}</td>
              <td className="px-3 py-2 text-right tnum">{(b.loss_rate * 100).toFixed(1)}%</td>
              <td className="px-3 py-2 text-right tnum">{b.invoice_count}</td>
              <td className="px-3 py-2 text-right tnum">{formatCurrency(b.gross_exposure, cur)}</td>
              <td className="px-3 py-2 text-right tnum text-[#FF3E00] font-semibold">{formatCurrency(b.ecl, cur)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid grid-cols-3 border border-zinc-200">
        <div className="border-r border-zinc-200 px-4 py-3"><div className="label-sm text-zinc-500">Gross Exposure</div><div className="font-display font-black text-xl tnum mt-1">{formatCurrency(d.total_gross_exposure, cur)}</div></div>
        <div className="border-r border-zinc-200 px-4 py-3"><div className="label-sm text-zinc-500">Total ECL Provision</div><div className="font-display font-black text-xl tnum mt-1 text-[#FF3E00]">{formatCurrency(d.total_ecl_provision, cur)}</div></div>
        <div className="px-4 py-3 bg-zinc-950 text-white"><div className="label-sm text-zinc-400">Coverage</div><div className="font-display font-black text-xl tnum mt-1">{d.coverage_pct}%</div></div>
      </div>
    </div>
  );
}

function IFRS15Panel({ cur }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [contracts, setContracts] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [form, setForm] = useState({ contract_no: "", customer: "", po: "", amount: "", start_date: "", end_date: "", recognition_method: "straight_line" });

  const load = async () => {
    const [c, s] = await Promise.all([
      api.get("/ifrs/ifrs15/contracts").then((r) => r.data),
      api.get(`/ifrs/ifrs15/schedule?year=${year}`).then((r) => r.data),
    ]);
    setContracts(c); setSchedule(s);
  };
  useEffect(() => { load().catch(() => {}); }, [year]);   

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ifrs/ifrs15/contracts", {
        contract_no: form.contract_no,
        customer: form.customer,
        currency: cur,
        lines: [{ performance_obligation: form.po, amount: Number(form.amount), start_date: form.start_date, end_date: form.end_date, recognition_method: form.recognition_method }],
      });
      toast.success("Contract created");
      setForm({ contract_no: "", customer: "", po: "", amount: "", start_date: "", end_date: "", recognition_method: "straight_line" });
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6" data-testid="ifrs15-panel">
      <div className="mb-4 flex items-baseline justify-between">
        <div className="text-xs font-mono text-zinc-400">REVENUE RECOGNITION · IFRS 15 · year {year}</div>
        <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="border-b border-zinc-300 bg-transparent py-1 text-sm w-24 tnum" data-testid="ifrs15-year" />
      </div>
      <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-7 gap-px bg-zinc-200 border border-zinc-950 mb-6" data-testid="ifrs15-form">
        <input value={form.contract_no} onChange={(e) => setForm({ ...form, contract_no: e.target.value })} required placeholder="Contract #" className="bg-white px-3 py-2.5 text-sm outline-none" />
        <input value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} required placeholder="Customer" className="bg-white px-3 py-2.5 text-sm outline-none" />
        <input value={form.po} onChange={(e) => setForm({ ...form, po: e.target.value })} required placeholder="Performance obligation" className="bg-white px-3 py-2.5 text-sm outline-none" />
        <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required placeholder="Amount" className="bg-white px-3 py-2.5 text-sm outline-none tnum" />
        <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required className="bg-white px-3 py-2.5 text-sm outline-none" />
        <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required className="bg-white px-3 py-2.5 text-sm outline-none" />
        <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-3 hover:bg-[#FF3E00]" data-testid="ifrs15-add">ADD</button>
      </form>
      {schedule && (
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-950">
            <tr className="text-xs uppercase">
              <th className="text-left px-3 py-2">Contract</th>
              <th className="text-left px-3 py-2">Customer</th>
              {Array.from({ length: 12 }, (_, i) => <th key={i + 1} className="text-right px-2 py-2">M{i + 1}</th>)}
              <th className="text-right px-3 py-2">Year Total</th>
            </tr>
          </thead>
          <tbody data-testid="ifrs15-schedule-tbody">
            {schedule.rows.map((r) => (
              <tr key={r.contract_no} className="border-b border-zinc-100">
                <td className="px-3 py-2 tnum text-xs">{r.contract_no}</td>
                <td className="px-3 py-2">{r.customer}</td>
                {Array.from({ length: 12 }, (_, i) => (
                  <td key={i + 1} className="px-2 py-2 text-right tnum text-xs">{r.monthly_totals[i + 1] > 0 ? formatCurrency(r.monthly_totals[i + 1], cur) : "—"}</td>
                ))}
                <td className="px-3 py-2 text-right tnum font-semibold">{formatCurrency(r.year_total, cur)}</td>
              </tr>
            ))}
            {schedule.rows.length === 0 && <tr><td colSpan={15} className="px-3 py-6 text-center text-zinc-400 text-sm">No contracts yet.</td></tr>}
            {schedule.rows.length > 0 && (
              <tr className="bg-zinc-950 text-white">
                <td colSpan={2} className="px-3 py-2 font-mono text-[10px]">GRAND TOTAL</td>
                {Array.from({ length: 12 }, (_, i) => (
                  <td key={i + 1} className="px-2 py-2 text-right tnum text-xs">{formatCurrency(schedule.grand_totals[i + 1], cur)}</td>
                ))}
                <td className="px-3 py-2 text-right tnum font-semibold">{formatCurrency(schedule.grand_total_year, cur)}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      <div className="mt-3 text-xs text-zinc-500">{contracts.length} contract(s) on file.</div>
    </div>
  );
}

function IFRS16Panel({ cur }) {
  const [leases, setLeases] = useState([]);
  const [selected, setSelected] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [form, setForm] = useState({ lease_no: "", lessor: "", start_date: "", end_date: "", monthly_payment: "", discount_rate: 0.05 });

  const load = async () => {
    const r = await api.get("/ifrs/ifrs16/leases");
    setLeases(r.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ifrs/ifrs16/leases", { ...form, monthly_payment: Number(form.monthly_payment), discount_rate: Number(form.discount_rate), currency: cur });
      toast.success("Lease created");
      setForm({ lease_no: "", lessor: "", start_date: "", end_date: "", monthly_payment: "", discount_rate: 0.05 });
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const view = async (id) => {
    setSelected(id);
    const r = await api.get(`/ifrs/ifrs16/leases/${id}/schedule`);
    setSchedule(r.data);
  };

  return (
    <div className="p-6" data-testid="ifrs16-panel">
      <div className="mb-4 text-xs font-mono text-zinc-400">LEASE LIABILITY + ROU ASSET · IFRS 16</div>
      <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-7 gap-px bg-zinc-200 border border-zinc-950 mb-6" data-testid="ifrs16-form">
        <input value={form.lease_no} onChange={(e) => setForm({ ...form, lease_no: e.target.value })} required placeholder="Lease #" className="bg-white px-3 py-2.5 text-sm outline-none" />
        <input value={form.lessor} onChange={(e) => setForm({ ...form, lessor: e.target.value })} required placeholder="Lessor" className="bg-white px-3 py-2.5 text-sm outline-none" />
        <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required className="bg-white px-3 py-2.5 text-sm outline-none" />
        <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required className="bg-white px-3 py-2.5 text-sm outline-none" />
        <input type="number" step="0.01" value={form.monthly_payment} onChange={(e) => setForm({ ...form, monthly_payment: e.target.value })} required placeholder="Monthly payment" className="bg-white px-3 py-2.5 text-sm outline-none tnum" />
        <input type="number" step="0.01" value={form.discount_rate} onChange={(e) => setForm({ ...form, discount_rate: e.target.value })} placeholder="Discount rate" title="Annual, e.g. 0.05" className="bg-white px-3 py-2.5 text-sm outline-none tnum" />
        <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-3 hover:bg-[#FF3E00]" data-testid="ifrs16-add">ADD</button>
      </form>

      <table className="w-full text-sm mb-5">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs uppercase">
            <th className="text-left px-3 py-2">Lease #</th>
            <th className="text-left px-3 py-2">Lessor</th>
            <th className="text-right px-3 py-2">Term (m)</th>
            <th className="text-right px-3 py-2">Monthly</th>
            <th className="text-right px-3 py-2">Initial Liability</th>
            <th className="text-right px-3 py-2">Initial ROU</th>
            <th />
          </tr>
        </thead>
        <tbody data-testid="ifrs16-tbody">
          {leases.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-zinc-400 text-sm">No leases yet.</td></tr>}
          {leases.map((l) => (
            <tr key={l.id} className={`border-b border-zinc-100 ${selected === l.id ? "bg-zinc-50" : ""}`}>
              <td className="px-3 py-2 tnum text-xs">{l.lease_no}</td>
              <td className="px-3 py-2">{l.lessor}</td>
              <td className="px-3 py-2 text-right tnum">{l.term_months}</td>
              <td className="px-3 py-2 text-right tnum">{formatCurrency(l.monthly_payment, l.currency || cur)}</td>
              <td className="px-3 py-2 text-right tnum font-semibold">{formatCurrency(l.initial_liability, l.currency || cur)}</td>
              <td className="px-3 py-2 text-right tnum">{formatCurrency(l.initial_rou, l.currency || cur)}</td>
              <td className="px-3 py-2"><button onClick={() => view(l.id)} className="text-[10px] font-mono border border-zinc-950 px-2 py-1 hover:bg-zinc-950 hover:text-white" data-testid={`ifrs16-view-${l.id}`}>SCHEDULE</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      {schedule && (
        <div className="border border-zinc-950" data-testid="ifrs16-schedule">
          <div className="bg-zinc-950 text-white px-4 py-2 flex items-center justify-between">
            <div className="font-mono text-[10px] tracking-widest">SCHEDULE · {schedule.lease_no} · {schedule.lessor}</div>
            <button onClick={() => { setSelected(null); setSchedule(null); }} className="text-[10px] font-mono">CLOSE ✕</button>
          </div>
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5">M</th>
                  <th className="text-right px-2 py-1.5">Payment</th>
                  <th className="text-right px-2 py-1.5">Interest</th>
                  <th className="text-right px-2 py-1.5">Principal</th>
                  <th className="text-right px-2 py-1.5">Closing Liability</th>
                  <th className="text-right px-2 py-1.5">Depreciation</th>
                  <th className="text-right px-2 py-1.5">Closing ROU</th>
                </tr>
              </thead>
              <tbody>
                {schedule.schedule.map((r) => (
                  <tr key={r.month} className="border-b border-zinc-100">
                    <td className="px-2 py-1 tnum">{r.month}</td>
                    <td className="px-2 py-1 text-right tnum">{formatCurrency(r.payment, cur)}</td>
                    <td className="px-2 py-1 text-right tnum">{formatCurrency(r.interest, cur)}</td>
                    <td className="px-2 py-1 text-right tnum">{formatCurrency(r.principal, cur)}</td>
                    <td className="px-2 py-1 text-right tnum">{formatCurrency(r.closing_liability, cur)}</td>
                    <td className="px-2 py-1 text-right tnum">{formatCurrency(r.depreciation, cur)}</td>
                    <td className="px-2 py-1 text-right tnum">{formatCurrency(r.closing_rou, cur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
